import { useMemo } from "react";
import { SEGMENTS, SEG_LABEL } from "@/lib/jackpot";

export const SPIN_MS = 6200;
const EASE = "cubic-bezier(0.1, 0.75, 0.12, 1)";
const STEP = 360 / SEGMENTS.length;
const B = SEGMENTS.indexOf("bankrupt");
const N = SEGMENTS.length;
const FILL = { plus: "url(#jr-pink)", minus: "#2a0d1f", double: "url(#jr-gold)", halve: "url(#jr-silver)", bankrupt: "#050505" };
const INK = { plus: "#fff", minus: "#f9a8d4", double: "#4a2a08", halve: "#1e1b2e", bankrupt: "#ef4444" };
const BIG = { double: 15, halve: 15 };

const slicePath = () => {
  const a0 = ((-STEP / 2 - 90) * Math.PI) / 180, a1 = ((STEP / 2 - 90) * Math.PI) / 180;
  return `M100,100 L${100 + 94 * Math.cos(a0)},${100 + 94 * Math.sin(a0)} A94,94 0 0,1 ${100 + 94 * Math.cos(a1)},${100 + 94 * Math.sin(a1)} Z`;
};

export function RouletteWheel({ rotation, spinning, ms = SPIN_MS, ease = EASE, tease = false }) {
  const d = useMemo(slicePath, []);
  return (
    <div className="relative w-full max-w-[18.5rem] sm:max-w-[23rem] aspect-square mx-auto" data-testid="jr-wheel" data-tease={tease ? "1" : "0"}>
      <div className={`absolute -inset-3 rounded-full ${tease ? "jr-halo-red" : "jr-halo"}`} />
      <svg viewBox="0 0 200 200" className="relative w-full h-full" style={{ transform: `rotate(${rotation}deg)`, transition: spinning ? `transform ${ms}ms ${ease}` : "none" }} data-testid="jr-wheel-disc">
        <defs>
          <linearGradient id="jr-gold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FEF3C7" /><stop offset=".45" stopColor="#FBBF24" /><stop offset="1" stopColor="#B45309" /></linearGradient>
          <linearGradient id="jr-pink" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#F472B6" /><stop offset="1" stopColor="#BE185D" /></linearGradient>
          <linearGradient id="jr-silver" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#F8FAFC" /><stop offset=".5" stopColor="#CBD5E1" /><stop offset="1" stopColor="#64748B" /></linearGradient>
          <radialGradient id="jr-hub"><stop offset="0" stopColor="#FEF3C7" /><stop offset="1" stopColor="#B45309" /></radialGradient>
        </defs>
        <circle cx="100" cy="100" r="99.5" fill="url(#jr-gold)" />
        <circle cx="100" cy="100" r="95.5" fill="#12040d" />
        {SEGMENTS.map((k, i) => (
          <g key={i} transform={`rotate(${(i + 0.5) * STEP} 100 100)`} data-testid={`jr-seg-${i}`}>
            <path d={d} fill={FILL[k]} stroke="#FBBF24" strokeOpacity=".55" strokeWidth=".7" />
            <text x="100" y={k === "bankrupt" ? 14 : 18} transform={`rotate(90 100 ${k === "bankrupt" ? 14 : 18})`} dominantBaseline="central" textAnchor="start"
              fontSize={k === "bankrupt" ? 8.6 : BIG[k] || 10.5} fontWeight="900" letterSpacing={k === "bankrupt" ? ".6" : "0"} fill={INK[k]} fontFamily="inherit">{SEG_LABEL[k]}</text>
          </g>
        ))}
        {SEGMENTS.map((_, i) => { const a = (i * STEP - 90) * Math.PI / 180; return <circle key={i} cx={100 + 97.5 * Math.cos(a)} cy={100 + 97.5 * Math.sin(a)} r="1.9" fill="#FFF7D6" className="wheel-bulb" style={{ animationDelay: `${i * 90}ms` }} />; })}
        <circle cx="100" cy="100" r="19" fill="url(#jr-hub)" stroke="#12040d" strokeWidth="2.5" />
        <text x="100" y="101" textAnchor="middle" dominantBaseline="central" fontSize="8" fontWeight="900" fill="#4a2a08" fontFamily="inherit">ITZ</text>
      </svg>
      {tease && (
        <div className="absolute inset-x-0 top-[60%] flex justify-center z-10 pointer-events-none">
          <span className="jr-tease-label px-4 py-1.5 rounded-full bg-red-600 text-white font-display text-lg font-black tracking-widest ring-2 ring-red-300" data-testid="jr-tease-label">BANKROTT?!</span>
        </div>
      )}
      <div className={`absolute left-1/2 -top-3 -translate-x-1/2 z-10 ${tease ? "jr-pointer-shake" : "jr-pointer"}`} data-testid="jr-wheel-pointer">
        <div className="w-0 h-0 border-l-[15px] border-r-[15px] border-t-[30px] border-l-transparent border-r-transparent border-t-amber-300 drop-shadow-[0_4px_10px_rgba(251,191,36,.8)]" />
      </div>
    </div>
  );
}

export const targetRotation = (current, kind) => {
  const options = SEGMENTS.map((k, i) => (k === kind ? i : -1)).filter((i) => i >= 0);
  const idx = options[Math.floor(Math.random() * options.length)];
  const base = current - (current % 360) + 360 * 8;
  return base + (360 - (idx + 0.5) * STEP) + (Math.random() - 0.5) * STEP * 0.55;
};

const TEASE_CHANCE = 0.3;

// steps for one spin; sometimes it stalls on BANKROTT and then slips into the real (neighbouring) result
export const spinPlan = (current, kind) => {
  const fwd = SEGMENTS[(B + N - 1) % N] === kind, back = SEGMENTS[(B + 1) % N] === kind;
  if (kind === "bankrupt" || (!fwd && !back) || Math.random() >= TEASE_CHANCE) return [{ rot: targetRotation(current, kind), ms: SPIN_MS, ease: EASE }];
  const dir = fwd ? 1 : -1;
  const p1 = fwd ? (B + 0.13) * STEP : (B + 0.87) * STEP;
  const r1 = current - (current % 360) + 360 * 8 + (360 - p1);
  const w = (f, ms) => ({ rot: r1 + dir * f * STEP, ms, ease: "ease-in-out", tease: true });
  return [
    { rot: r1, ms: SPIN_MS + 700, ease: EASE },
    { ...w(0.07, 260), sfx: "suspense" }, w(0.01, 300), w(0.09, 280), w(0.03, 340),
    { rot: r1 + dir * 0.45 * STEP, ms: 1250, ease: "cubic-bezier(.55,0,.25,1)", tease: true, sfx: "whoosh", release: 520 },
  ];
};
